import React, { useState, useEffect } from 'react';
import { ref, onValue, set, remove } from 'firebase/database';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { db, secondaryAuth } from '../firebase';

const inputStyle = {
  background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: '0.5rem',
  color: 'white', padding: '0.5rem 0.75rem', fontSize: '0.9rem', width: '100%',
};

export default function ManageManagers({ inventory }) {
  const [entries, setEntries] = useState({}); // uid -> { role, name, email, kiosks, created_at }
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedKiosks, setSelectedKiosks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingUid, setEditingUid] = useState(null);
  const [editKiosks, setEditKiosks] = useState([]);

  const kioskIds = Object.keys(inventory || {});

  useEffect(() => {
    const unsub = onValue(ref(db, 'user_roles'), (snap) => {
      setEntries(snap.val() || {});
    });
    return unsub;
  }, []);

  const managers = Object.entries(entries).filter(([, v]) => v?.role === 'manager');
  const revoked  = Object.entries(entries).filter(([, v]) => v?.role === 'revoked');

  const toggleKiosk = (kioskId) => {
    setSelectedKiosks((prev) =>
      prev.includes(kioskId) ? prev.filter((k) => k !== kioskId) : [...prev, kioskId]
    );
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (selectedKiosks.length === 0) {
      setError('Select at least one kiosk for this manager.');
      return;
    }
    setSubmitting(true);
    try {
      const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      const uid = cred.user.uid;
      await set(ref(db, `user_roles/${uid}`), {
        role: 'manager',
        name,
        email,
        kiosks: selectedKiosks,
        created_at: new Date().toISOString(),
      });
      await signOut(secondaryAuth); // clean up the secondary session; admin's own session is untouched
      setSuccess(`Manager account created for ${name} (${email}).`);
      setName('');
      setEmail('');
      setPassword('');
      setSelectedKiosks([]);
    } catch (err) {
      setError(err.message || 'Failed to create manager account.');
    }
    setSubmitting(false);
  };

  const revokeManager = (uid, entry) => {
    if (!window.confirm(`Revoke access for ${entry.email}?`)) return;
    set(ref(db, `user_roles/${uid}`), { ...entry, role: 'revoked' });
  };

  const restoreManager = (uid, entry) => {
    set(ref(db, `user_roles/${uid}`), { ...entry, role: 'manager' });
  };

  const startEdit = (uid, entry) => {
    setEditingUid(uid);
    setEditKiosks(entry.kiosks || []);
  };

  const cancelEdit = () => {
    setEditingUid(null);
    setEditKiosks([]);
  };

  const toggleEditKiosk = (kioskId) => {
    setEditKiosks((prev) =>
      prev.includes(kioskId) ? prev.filter((k) => k !== kioskId) : [...prev, kioskId]
    );
  };

  const saveEdit = (uid, entry) => {
    set(ref(db, `user_roles/${uid}`), { ...entry, kiosks: editKiosks });
    setEditingUid(null);
    setEditKiosks([]);
  };

  const deleteEntry = (uid, entry) => {
    // IMPORTANT: an entry with NO role record at all is treated as admin
    // (unrestricted) by the app. Deleting this record only removes the
    // *database* entry — it does NOT delete the underlying Firebase Auth
    // account. If that Auth account still exists, deleting this entry would
    // hand it full admin access. Only do this after deleting the Auth
    // account itself (Firebase Console → Authentication), e.g. when
    // freeing up an email to reuse for a new manager.
    const ok = window.confirm(
      `Permanently delete this record for ${entry.email}?\n\n` +
      `Only do this if you've ALREADY deleted their account in Firebase Console → Authentication. ` +
      `If that login still exists, deleting this record would give it full admin access (no record = admin).`
    );
    if (!ok) return;
    remove(ref(db, `user_roles/${uid}`));
  };

  return (
    <div className="section">
      <div className="section-header">
        <h2 className="section-title">
          <span className="section-title-icon">👥</span>
          Manage Managers
        </h2>
        <p style={{ color: '#666', fontSize: '0.8rem', margin: 0 }}>
          Manager logins see only their assigned kiosk(s) on the Dashboard tab — no other tabs, no other kiosks.
        </p>
      </div>

      {/* ── Add manager form ─────────────────────────────────────────────── */}
      <form onSubmit={handleCreate} style={{
        background: 'rgba(255,255,255,0.03)', border: '1px solid #2a2a4a',
        borderRadius: '0.75rem', padding: '1.25rem', marginBottom: '1.5rem',
      }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', color: 'var(--text-primary)' }}>Add Manager</h3>

        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ display: 'block', color: '#aaa', fontSize: '0.8rem', marginBottom: '0.4rem' }}>Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Ramesh Kumar" required style={inputStyle} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={{ display: 'block', color: '#aaa', fontSize: '0.8rem', marginBottom: '0.4rem' }}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', color: '#aaa', fontSize: '0.8rem', marginBottom: '0.4rem' }}>Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              required minLength={6} style={inputStyle} />
          </div>
        </div>

        <label style={{ display: 'block', color: '#aaa', fontSize: '0.8rem', marginBottom: '0.4rem' }}>
          Assigned Kiosks
        </label>
        {kioskIds.length === 0 ? (
          <p style={{ color: '#666', fontSize: '0.85rem' }}>No kiosks found in inventory yet.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '1rem' }}>
            {kioskIds.map((kioskId) => (
              <label key={kioskId} style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                background: selectedKiosks.includes(kioskId) ? 'rgba(0,255,153,0.12)' : '#1a1a2e',
                border: `1px solid ${selectedKiosks.includes(kioskId) ? '#00ff99' : '#2a2a4a'}`,
                borderRadius: '0.5rem', padding: '0.4rem 0.75rem', fontSize: '0.85rem', cursor: 'pointer',
              }}>
                <input type="checkbox" checked={selectedKiosks.includes(kioskId)}
                  onChange={() => toggleKiosk(kioskId)} />
                {kioskId}
              </label>
            ))}
          </div>
        )}

        {error && <p style={{ color: '#ff6b6b', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{error}</p>}
        {success && <p style={{ color: '#00ff99', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{success}</p>}

        <button type="submit" disabled={submitting} style={{
          background: submitting ? '#333' : 'linear-gradient(135deg, #00ff99, #00bfff)',
          color: submitting ? '#888' : '#000', border: 'none', borderRadius: '0.6rem',
          padding: '0.6rem 1.5rem', fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '0.9rem',
        }}>
          {submitting ? 'Creating…' : '+ Create Manager Account'}
        </button>
      </form>

      {/* ── Existing managers ────────────────────────────────────────────── */}
      <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', color: 'var(--text-primary)' }}>Active Managers</h3>
      {managers.length === 0 ? (
        <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '1.5rem' }}>No managers yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
          {managers.map(([uid, entry]) => (
            <div key={uid} style={{
              background: 'rgba(255,255,255,0.03)', border: '1px solid #2a2a4a',
              borderRadius: '0.6rem', padding: '0.6rem 1rem',
            }}>
              {editingUid === uid ? (
                <div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.6rem' }}>
                    Editing kiosks for {entry.name || entry.email}
                  </div>
                  {kioskIds.length === 0 ? (
                    <p style={{ color: '#666', fontSize: '0.85rem' }}>No kiosks found in inventory yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.75rem' }}>
                      {kioskIds.map((kioskId) => (
                        <label key={kioskId} style={{
                          display: 'flex', alignItems: 'center', gap: '0.4rem',
                          background: editKiosks.includes(kioskId) ? 'rgba(0,255,153,0.12)' : '#1a1a2e',
                          border: `1px solid ${editKiosks.includes(kioskId) ? '#00ff99' : '#2a2a4a'}`,
                          borderRadius: '0.5rem', padding: '0.4rem 0.75rem', fontSize: '0.85rem', cursor: 'pointer',
                        }}>
                          <input type="checkbox" checked={editKiosks.includes(kioskId)}
                            onChange={() => toggleEditKiosk(kioskId)} />
                          {kioskId}
                        </label>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => saveEdit(uid, entry)} style={{
                      background: 'linear-gradient(135deg, #00ff99, #00bfff)', color: '#000', border: 'none',
                      borderRadius: '0.5rem', padding: '0.35rem 0.85rem', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
                    }}>
                      Save
                    </button>
                    <button onClick={cancelEdit} style={{
                      background: 'transparent', color: '#aaa', border: '1px solid #2a2a4a',
                      borderRadius: '0.5rem', padding: '0.35rem 0.85rem', fontSize: '0.8rem', cursor: 'pointer',
                    }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>{entry.name || entry.email}</div>
                    <div style={{ color: '#666', fontSize: '0.78rem' }}>
                      {entry.name && `${entry.email} · `}{(entry.kiosks || []).join(', ') || 'No kiosks assigned'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => startEdit(uid, entry)} style={{
                      background: 'rgba(0,191,255,0.12)', color: '#00bfff', border: '1px solid rgba(0,191,255,0.3)',
                      borderRadius: '0.5rem', padding: '0.35rem 0.85rem', fontSize: '0.8rem', cursor: 'pointer',
                    }}>
                      Edit Kiosks
                    </button>
                    <button onClick={() => revokeManager(uid, entry)} style={{
                      background: 'rgba(255,68,68,0.12)', color: '#ff6b6b', border: '1px solid rgba(255,68,68,0.3)',
                      borderRadius: '0.5rem', padding: '0.35rem 0.85rem', fontSize: '0.8rem', cursor: 'pointer',
                    }}>
                      Revoke Access
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {revoked.length > 0 && (
        <>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', color: 'var(--text-primary)' }}>Revoked</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {revoked.map(([uid, entry]) => (
              <div key={uid} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: 'rgba(255,255,255,0.02)', border: '1px solid #2a2a4a',
                borderRadius: '0.6rem', padding: '0.6rem 1rem', opacity: 0.7,
              }}>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.9rem' }}>{entry.email}</div>
                  <div style={{ color: '#555', fontSize: '0.78rem' }}>{(entry.kiosks || []).join(', ')}</div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => restoreManager(uid, entry)} style={{
                    background: 'rgba(0,255,153,0.1)', color: '#00ff99', border: '1px solid rgba(0,255,153,0.3)',
                    borderRadius: '0.5rem', padding: '0.35rem 0.85rem', fontSize: '0.8rem', cursor: 'pointer',
                  }}>
                    Restore Access
                  </button>
                  <button onClick={() => deleteEntry(uid, entry)} style={{
                    background: 'transparent', color: '#666', border: '1px solid #2a2a4a',
                    borderRadius: '0.5rem', padding: '0.35rem 0.85rem', fontSize: '0.8rem', cursor: 'pointer',
                  }}>
                    Delete Permanently
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
